import * as React from "react"
import { DayPicker, type Locale } from "react-day-picker"
import { fr, ar, enUS } from "react-day-picker/locale"
import { useTranslation } from "react-i18next"
import { ChevronLeft, ChevronRight } from "lucide-react"

import { cn } from "../../lib/utils"

const LOCALES: Record<string, Locale> = { fr, ar, en: enUS }

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}: React.ComponentProps<typeof DayPicker>) {
  const { i18n } = useTranslation()
  const locale = LOCALES[i18n.language] ?? fr

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      locale={locale}
      dir={i18n.language === "ar" ? "rtl" : "ltr"}
      className={cn("p-3", className)}
      classNames={{
        months: "relative flex flex-col sm:flex-row gap-2",
        month: "flex flex-col gap-2",
        month_caption: "flex items-center justify-center h-7 text-sm font-medium",
        caption_label: "text-sm font-medium",
        nav: "absolute top-0 inset-x-0 flex items-center justify-between pointer-events-none z-10",
        button_previous: cn(
          "inline-flex size-7 items-center justify-center rounded-md opacity-70 hover:opacity-100 hover:bg-accent hover:text-accent-foreground transition-opacity disabled:pointer-events-none disabled:opacity-30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring pointer-events-auto",
        ),
        button_next: cn(
          "inline-flex size-7 items-center justify-center rounded-md opacity-70 hover:opacity-100 hover:bg-accent hover:text-accent-foreground transition-opacity disabled:pointer-events-none disabled:opacity-30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring pointer-events-auto",
        ),
        month_grid: "w-full border-collapse",
        weekdays: "",
        weekday: "text-muted-foreground w-9 h-9 text-center align-middle font-normal text-[0.8rem]",
        week: "",
        day: cn(
          "w-9 h-9 text-center align-middle text-sm p-0 relative focus-within:relative focus-within:z-20",
          "[&:has([aria-selected])]:bg-accent",
          "[&:has([aria-selected].day-outside)]:bg-accent/50",
          "[&:has([aria-selected].day-range-end)]:rounded-r-md",
        ),
        day_button: cn(
          "inline-flex w-9 h-9 items-center justify-center rounded-md text-sm font-normal whitespace-nowrap transition-colors",
          "hover:bg-accent hover:text-accent-foreground",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background",
          "disabled:pointer-events-none disabled:opacity-50",
          "aria-selected:opacity-100",
        ),
        range_end: "day-range-end",
        selected:
          "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground rounded-md",
        today: "bg-accent text-accent-foreground rounded-md",
        outside:
          "day-outside text-muted-foreground opacity-50 aria-selected:bg-accent/50 aria-selected:text-muted-foreground",
        disabled: "text-muted-foreground opacity-50",
        range_middle:
          "aria-selected:bg-accent aria-selected:text-accent-foreground",
        hidden: "invisible",
        ...classNames,
      }}
      components={{
        Chevron: ({ orientation }) =>
          orientation === "left" ? (
            <ChevronLeft className="size-4" />
          ) : (
            <ChevronRight className="size-4" />
          ),
      }}
      {...props}
    />
  )
}

export { Calendar }
